import { db } from '../firebase/firebaseConfig';
import { ref, push, onValue, set, get } from 'firebase/database';
import { encryptMessage, decryptMessage } from './cryptoService';
import { getSimulatedResponse } from './aiChatService';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Generate a verifiable hex-based chat session ID
export const generateDTAI_ID = () => {
  const hex = Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0').toUpperCase();
  return `DTAI-${hex}`;
};

// Send and Encrypt a User Message
export const sendEncryptedMessage = async (uid, sessionId, text, sender = 'user') => {
  const encryptedText = await encryptMessage(uid, text);
  if (!encryptedText) throw new Error("Encryption failed.");

  const chatRef = ref(db, `chats/${uid}/${sessionId}/messages`);
  const newMsgRef = push(chatRef);
  
  await set(newMsgRef, {
    sender,
    text: encryptedText, // E2EE Payload
    timestamp: Date.now()
  });
};

// Listen and Decrypt Chat History
export const listenToChat = (uid, sessionId, callback) => {
  const chatRef = ref(db, `chats/${uid}/${sessionId}/messages`);
  
  return onValue(chatRef, async (snapshot) => {
    if (snapshot.exists()) {
      const msgs = [];
      const data = snapshot.val();
      for (const key in data) {
        const msg = data[key];
        // Decrypt payload dynamically
        const plainText = await decryptMessage(uid, msg.text);
        msgs.push({ id: key, sender: msg.sender, text: plainText, timestamp: msg.timestamp });
      }
      callback(msgs.sort((a, b) => a.timestamp - b.timestamp));
    } else {
      callback([]);
    }
  });
};

// Fetch User Chat Sessions (Sidebar History)
export const getUserChatSessions = async (uid) => {
  const chatsRef = ref(db, `chats/${uid}`);
  const snapshot = await get(chatsRef);
  if (snapshot.exists()) {
    const data = snapshot.val();
    return Object.keys(data).map(sessionId => {
      const messagesObj = data[sessionId].messages || {};
      const msgKeys = Object.keys(messagesObj);
      const lastMsg = msgKeys.length > 0 ? messagesObj[msgKeys[msgKeys.length - 1]] : null;
      return {
        sessionId,
        updatedAt: lastMsg ? lastMsg.timestamp : 0
      };
    }).sort((a, b) => b.updatedAt - a.updatedAt);
  }
  return [];
};

// Fetch All Chats (Admin Only)
export const getAllChats = async () => {
  const chatsRef = ref(db, `chats`);
  const snapshot = await get(chatsRef);
  if (snapshot.exists()) {
    const data = snapshot.val();
    const allSessions = [];
    for (const uid in data) {
      for (const sessionId in data[uid]) {
        const messagesObj = data[uid][sessionId].messages || {};
        const msgKeys = Object.keys(messagesObj);
        const lastMsg = msgKeys.length > 0 ? messagesObj[msgKeys[msgKeys.length - 1]] : null;
        allSessions.push({
          uid,
          sessionId,
          updatedAt: lastMsg ? lastMsg.timestamp : 0,
          previewText: lastMsg ? await decryptMessage(uid, lastMsg.text) : 'New Session'
        });
      }
    }
    return allSessions.sort((a, b) => b.updatedAt - a.updatedAt);
  }
  return [];
};

// Process AI Response (Simulated)
export const processAIResponse = async (uid, sessionId, userMsg, stats) => {
  // Simulate Thinking Time (1500ms - 3000ms)
  const delay = Math.floor(Math.random() * 1500) + 1500;
  await sleep(delay);

  // Generate Response
  const aiText = getSimulatedResponse(userMsg, stats);

  // Send as AI
  await sendEncryptedMessage(uid, sessionId, aiText, 'ai');
};
